// components/SystemSelectorModal.tsx
import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  MenuItem,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BWITextField from './shared/BWITextField';
import { System } from '../state/types';

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (systemCode: string) => void;
  systems: System[];
  selectedSystem: string;
  accessibleSystemCodes: string[];
}

interface GroupedSystem {
  label: string;
  systems: { code: string; label: string }[];
}

const GROUP_RULES: { label: string; prefixes: string[] }[] = [
  { label: "Car Dealers", prefixes: ["POAGE", "MENHG", "OCTN", "OVRSTK", "NISSAN"] },
  { label: "Alliance Auto Auctions", prefixes: ["ALNCAA"] },
  { label: "Dealers Auto Auctions", prefixes: ["DLRSAA"] },
  { label: "Manheim Auto Auctions", prefixes: ["MANHEM"] },
  { label: "Metro Auto Auctions", prefixes: ["METRAA", "MAA"] },
  { label: "Boats", prefixes: ["BRLTTA", "NTCSTR", "NAUTIQ", "STNG"] },
  { label: "RV", prefixes: ["BRNKLY", "FRSTRV"] },
  { label: "Internal", prefixes: ["BWI"] },
];

const SystemSelectorModal: React.FC<Props> = ({
  open,
  onClose,
  onSelect,
  systems,
  selectedSystem,
  accessibleSystemCodes,
}) => {
  const [search, setSearch] = React.useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open]);

  const groupAndFilterSystems = (
    systemCodes: string[],
    systems: System[],
    search: string
  ): GroupedSystem[] => {
    const groupMap: { [key: string]: GroupedSystem } = {};
    const otherGroup: GroupedSystem = { label: "Other", systems: [] };

    // Initialize groups
    GROUP_RULES.forEach(({ label }) => {
      groupMap[label] = { label, systems: [] };
    });

    systemCodes.forEach((code) => {
      const system = systems.find((s) => s.systemCode === code);
      const name = system?.commonName || "Unknown";

      if (
        !code.toLowerCase().includes(search.toLowerCase()) &&
        !name.toLowerCase().includes(search.toLowerCase())
      ) {
        return;
      }
      const label = `${name} (${code})`;

      const matchedGroup = GROUP_RULES.find(({ prefixes }) =>
        prefixes.some((prefix) => code.startsWith(prefix))
      );

      if (matchedGroup) {
        groupMap[matchedGroup.label].systems.push({ code, label });
      } else {
        otherGroup.systems.push({ code, label });
      }
    });

    // Sort systems within each group
    Object.values(groupMap).forEach((group) =>
      group.systems.sort((a, b) => a.label.localeCompare(b.label))
    );
    otherGroup.systems.sort((a, b) => a.label.localeCompare(b.label));

    const allGroups = [...Object.values(groupMap), otherGroup];
    return allGroups.filter((group) => group.systems.length > 0);
  };

  if (!systems || systems.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Select a System</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Loading systems...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select a System</DialogTitle>
      <DialogContent dividers>
        <BWITextField
          startIcon={<SearchIcon />}
          placeholder="Search Systems..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputRef={searchInputRef}
          fullWidth
          sx={{ mb: 2 }}
        />
        {groupAndFilterSystems(accessibleSystemCodes, systems, search).map((group) => (
          <Box key={group.label} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {group.label}
            </Typography>
            {group.systems.map(({ code, label }) => (
              <MenuItem
                key={code}
                selected={code === selectedSystem}
                onClick={() => {
                  onSelect(code);
                  onClose();
                }}
              >
                <Typography variant="body1">{label}</Typography>
              </MenuItem>
            ))}
          </Box>
        ))}
        {groupAndFilterSystems(accessibleSystemCodes, systems, search).length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No systems match your search.
          </Typography>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemSelectorModal;
